/**
 * PWA Service Worker Registration
 * 
 * This module handles the registration and lifecycle of the service worker
 * for Progressive Web App functionality.
 */

import { registerSW } from 'virtual:pwa-register';

export interface PWAUpdateInfo {
  needRefresh: boolean;
  offlineReady: boolean;
  updateServiceWorker: () => Promise<void>;
}

/**
 * Register the service worker and handle updates
 * @returns PWA update information and control functions
 */
export const registerPWA = (): PWAUpdateInfo => {
  let updateSW: ((reloadPage?: boolean) => Promise<void>) | undefined;
  let needRefresh = false;
  let offlineReady = false;

  const updateServiceWorker = async () => {
    if (updateSW) {
      await updateSW(true);
    }
  };

  // Register service worker with auto-update
  updateSW = registerSW({
    immediate: true,
    onNeedRefresh() {
      needRefresh = true;
      console.log('New content available, please refresh.');
      
      // Optionally show a toast notification
      if ('Notification' in window && Notification.permission === 'granted') {
        new Notification('Update Available', {
          body: 'A new version of PawsIndia is available. Refresh to update.',
          icon: '/icons/icon-192x192.png',
          badge: '/icons/icon-96x96.png',
        });
      }
    },
    onOfflineReady() {
      offlineReady = true;
      console.log('App ready to work offline');
    },
    onRegistered(registration) {
      console.log('Service Worker registered:', registration);
      
      // Check for updates every hour
      if (registration) {
        setInterval(() => {
          registration.update();
        }, 60 * 60 * 1000); // 1 hour
      }
    },
    onRegisterError(error) {
      console.error('Service Worker registration error:', error);
    },
  });

  return {
    needRefresh,
    offlineReady,
    updateServiceWorker,
  };
};

/**
 * Check if the app is running as a PWA (installed)
 */
export const isPWA = (): boolean => {
  return (
    window.matchMedia('(display-mode: standalone)').matches ||
    (window.navigator as any).standalone === true ||
    document.referrer.includes('android-app://')
  );
};

/**
 * Check if the device is iOS
 */
export const isIOS = (): boolean => {
  return /iPad|iPhone|iPod/.test(navigator.userAgent) && !(window as any).MSStream;
};

/**
 * Check if the device is Android
 */
export const isAndroid = (): boolean => {
  return /Android/.test(navigator.userAgent);
};

/**
 * Check if the browser supports PWA installation
 */
export const supportsPWA = (): boolean => {
  return 'serviceWorker' in navigator && 'PushManager' in window;
};

/**
 * Get device type for analytics
 */
export const getDeviceType = (): 'mobile' | 'tablet' | 'desktop' => {
  const width = window.innerWidth;
  if (width < 768) return 'mobile';
  if (width < 1024) return 'tablet';
  return 'desktop';
};

/**
 * Get connection type for adaptive loading
 */
export const getConnectionType = (): string => {
  const connection = (navigator as any).connection || (navigator as any).mozConnection || (navigator as any).webkitConnection;
  return connection?.effectiveType || 'unknown';
};

/**
 * Check if the connection is slow
 */
export const isSlowConnection = (): boolean => {
  const connection = (navigator as any).connection || (navigator as any).mozConnection || (navigator as any).webkitConnection;
  if (!connection) return false;
  
  const slowTypes = ['slow-2g', '2g', '3g'];
  return slowTypes.includes(connection.effectiveType);
};
